import {
    assertSketchEntityPersistentId,
    assertSketchPersistentId,
    parseSketchSplineEvaluationDefinition,
    parseSketchSplineMetadataRecord,
} from '../../../sketch';
import type {SketchEntityReferenceRecord} from './SketchEntityReferenceRecord';

/** 转换实体引用允许持久化的诊断类别。 */
const EXECUTION_DIAGNOSTIC_KINDS = new Set([
    'tag-write-failed',
    'tag-cleanup-failed',
    'source-missing-cleanup',
    'source-missing',
    'source-ambiguous',
    'target-missing-cleanup',
    'rebuild-failed',
    'input-invalid',
    'unsupported-source',
    'projection-degenerate',
    'projection-invalid',
    'restore-skipped',
    'restore-failed',
    'relation-solve-failed',
    'compensation-failed',
    'arc-projection-degenerate',
    'arc-projection-non-parallel',
    'circle-projection-degenerate',
    'ellipse-projection-degenerate',
    'owning-feature-missing',
    'feature-edge-missing',
    'create-batch-failed',
    'cascade-remove-failed',
    'target-dispose-failed',
    'unsupported-curve-type',
    'ellipse-arc-projection-degenerate',
    'style-spline-projection-degenerate',
    'unsafe-rebind',
    'sampled-curve-fit-warning',
    'sampled-curve-fit-failed',
    'selection-chain-branch-stopped',
    'selection-chain-target-sketch-rejected',
]);

/** topology owner 允许持久化的判别值。 */
const TOPOLOGY_OWNER_KINDS = new Set([
    'feature-result',
    'model-body',
    'imported-model-body',
]);

/** Puppy CAD topology 快照允许持久化的曲线类别。 */
const TOPOLOGY_CURVE_KINDS = new Set([
    'line',
    'circle',
    'ellipse',
    'hyperbola',
    'parabola',
    'bezier',
    'bspline',
    'offset',
    'other',
]);

/** Puppy CAD topology 快照允许持久化的曲面类别。 */
const TOPOLOGY_SURFACE_KINDS = new Set([
    'plane',
    'cylinder',
    'cone',
    'sphere',
    'torus',
    'bezier',
    'bspline',
    'surface-of-revolution',
    'surface-of-extrusion',
    'offset',
    'other',
]);

/** topology 边来源允许持久化的解析等级。 */
const TOPOLOGY_RESOLUTION_LEVELS = new Set([
    'exact',
    'fitted-exact',
    'sampled-only',
    'unsupported',
]);

/** sampled-only 曲线允许持久化的拟合表达。 */
const TOPOLOGY_FIT_KINDS = new Set([
    'line',
    'circle',
    'arc',
    'ellipse',
    'ellipse-arc',
    'parabola',
    'spline-approximation',
]);

/** sampled-only 曲线允许持久化的拟合方法。 */
const TOPOLOGY_FIT_METHODS = new Set([
    'analytic-fit',
    'spline-approximation',
]);

/**
 * 判断未知值是否为普通记录对象。
 *
 * @param value 未知值。
 * @returns 是否为可读记录对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

/**
 * 判断值是否仅包含可持久化的 JSON plain data。
 *
 * @param value 未知值。
 * @returns 是否为可序列化 plain data。
 */
function isSerializablePlainData(value: unknown): boolean {
    if (value === null || typeof value === 'string' || typeof value === 'boolean') {
        return true;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }
    if (Array.isArray(value)) {
        return value.every(isSerializablePlainData);
    }
    if (!isRecord(value)) {
        return false;
    }
    return Object.values(value).every((item) => item === undefined || isSerializablePlainData(item));
}

/**
 * 读取非空字符串字段。
 *
 * @param value 未知字段值。
 * @param path 字段路径。
 * @returns 原始字符串。
 */
function parseNonEmptyString(value: unknown, path: string): string {
    if (typeof value !== 'string' || value.trim().length === 0) {
        throw new Error(`转换实体引用 Record 字段必须是非空字符串: ${path}`);
    }
    return value;
}

/**
 * 校验可选非空字符串。
 *
 * @param value 未知字段值。
 * @param path 字段路径。
 */
function assertOptionalNonEmptyString(value: unknown, path: string): void {
    if (value === undefined) {
        return;
    }
    parseNonEmptyString(value, path);
}

/**
 * 校验允许显式 null 的可选非空字符串。
 *
 * @param value 未知字段值。
 * @param path 字段路径。
 */
function assertOptionalNullableNonEmptyString(value: unknown, path: string): void {
    if (value === undefined || value === null) {
        return;
    }
    parseNonEmptyString(value, path);
}

/**
 * 校验可选非空字符串数组。
 *
 * @param value 未知字段值。
 * @param path 字段路径。
 */
function assertOptionalStringArray(value: unknown, path: string): void {
    if (value === undefined) {
        return;
    }
    if (!Array.isArray(value)) {
        throw new Error(`转换实体引用 Record 字符串数组无效: ${path}`);
    }
    value.forEach((item, index) => parseNonEmptyString(item, `${path}[${index}]`));
}

/**
 * 校验字段是否属于指定字符串集合。
 *
 * @param value 未知字段值。
 * @param allowedValues 允许值集合。
 * @param path 字段路径。
 */
function assertStringEnum(
    value: unknown,
    allowedValues: ReadonlySet<string>,
    path: string,
): void {
    if (typeof value !== 'string' || !allowedValues.has(value)) {
        throw new Error(`转换实体引用 Record 枚举字段无效: ${path}`);
    }
}

/**
 * 校验非负整数。
 *
 * @param value 未知字段值。
 * @param path 字段路径。
 */
function assertNonNegativeInteger(value: unknown, path: string): void {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
        throw new Error(`转换实体引用 Record 非负整数字段无效: ${path}`);
    }
}

/**
 * 校验正整数。
 *
 * @param value 未知字段值。
 * @param path 字段路径。
 */
function assertPositiveInteger(value: unknown, path: string): void {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
        throw new Error(`转换实体引用 Record 正整数字段无效: ${path}`);
    }
}

/**
 * 断言值是有限数字。
 *
 * @param value 未知值。
 * @param path 字段路径。
 */
function assertFiniteNumber(value: unknown, path: string): void {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(`转换实体引用 Record geometryPayload 数字无效: ${path}`);
    }
}

/**
 * 断言值是三维有限世界坐标。
 *
 * @param value 未知值。
 * @param path 字段路径。
 */
function assertWorldCoordinate(value: unknown, path: string): void {
    if (!Array.isArray(value) || value.length !== 3) {
        throw new Error(`转换实体引用 Record geometryPayload 坐标无效: ${path}`);
    }
    value.forEach((coordinate, index) => assertFiniteNumber(coordinate, `${path}[${index}]`));
}

/**
 * 校验重建几何载荷中会被现有 Builder 直接读取的必需字段。
 *
 * @param kind 投影结果类别。
 * @param payload 几何载荷。
 * @param path 字段路径。
 */
function assertGeometryPayload(kind: string, payload: Record<string, unknown>, path: string): void {
    if (kind === 'line') {
        assertWorldCoordinate(payload.startWorld, `${path}.startWorld`);
        assertWorldCoordinate(payload.endWorld, `${path}.endWorld`);
        return;
    }
    if (kind === 'circle') {
        assertWorldCoordinate(payload.centerWorld, `${path}.centerWorld`);
        assertFiniteNumber(payload.radius, `${path}.radius`);
        return;
    }
    if (kind === 'ellipse') {
        assertWorldCoordinate(payload.centerWorld, `${path}.centerWorld`);
        assertFiniteNumber(payload.radiusX, `${path}.radiusX`);
        assertFiniteNumber(payload.radiusY, `${path}.radiusY`);
        assertFiniteNumber(payload.rotation, `${path}.rotation`);
        return;
    }
    if (kind === 'arc' || kind === 'ellipse-arc') {
        assertWorldCoordinate(payload.centerWorld, `${path}.centerWorld`);
        if (kind === 'arc') {
            assertFiniteNumber(payload.radius, `${path}.radius`);
        } else {
            assertFiniteNumber(payload.radiusX, `${path}.radiusX`);
            assertFiniteNumber(payload.radiusY, `${path}.radiusY`);
            assertFiniteNumber(payload.rotation, `${path}.rotation`);
        }
        assertFiniteNumber(payload.startAngle, `${path}.startAngle`);
        assertFiniteNumber(payload.endAngle, `${path}.endAngle`);
        if (typeof payload.clockwise !== 'boolean') {
            throw new Error(`转换实体引用 Record geometryPayload 布尔字段无效: ${path}.clockwise`);
        }
        return;
    }
    if (kind === 'parabola') {
        assertWorldCoordinate(payload.focalWorld, `${path}.focalWorld`);
        assertWorldCoordinate(payload.apexWorld, `${path}.apexWorld`);
        assertFiniteNumber(payload.startParameter, `${path}.startParameter`);
        assertFiniteNumber(payload.endParameter, `${path}.endParameter`);
        if (typeof payload.segments !== 'number' || !Number.isInteger(payload.segments) || payload.segments < 1) {
            throw new Error(`转换实体引用 Record geometryPayload 段数无效: ${path}.segments`);
        }
        if (!Array.isArray(payload.curvePointWorlds)) {
            throw new Error(`转换实体引用 Record geometryPayload 点列无效: ${path}.curvePointWorlds`);
        }
        payload.curvePointWorlds.forEach((point, index) => (
            assertWorldCoordinate(point, `${path}.curvePointWorlds[${index}]`)
        ));
        return;
    }
    if (kind === 'spline' || kind === 'style-spline') {
        if (!Array.isArray(payload.controlPointWorlds) || !Array.isArray(payload.curvePointWorlds)) {
            throw new Error(`转换实体引用 Record geometryPayload 点列无效: ${path}`);
        }
        payload.controlPointWorlds.forEach((point, index) => (
            assertWorldCoordinate(point, `${path}.controlPointWorlds[${index}]`)
        ));
        payload.curvePointWorlds.forEach((point, index) => (
            assertWorldCoordinate(point, `${path}.curvePointWorlds[${index}]`)
        ));
        /**
         * 转换引用与草图实体最终由同一组 Spline Builder 消费，必须复用草图持久化
         * 的权威解析规则，避免两条离线入口接受不同的曲线结构。
         */
        parseSketchSplineMetadataRecord(
            payload.curveMetadata,
            `${path}.curveMetadata`,
        );
        if (payload.curveEvaluationDefinition !== undefined) {
            parseSketchSplineEvaluationDefinition(
                payload.curveEvaluationDefinition,
                `${path}.curveEvaluationDefinition`,
            );
        }
    }
}

/**
 * 校验可选投影状态。
 *
 * @param value 未知投影状态。
 * @param projectionResultKind Record 的投影结果类别。
 * @param path 字段路径。
 */
function assertProjectionState(value: unknown, projectionResultKind: string, path: string): void {
    if (value === undefined) {
        return;
    }
    if (!isRecord(value) || value.projectionResultKind !== projectionResultKind) {
        throw new Error(`转换实体引用 Record projectionState 无效: ${path}`);
    }
    if (projectionResultKind === 'line') {
        if (typeof value.startScalar !== 'number' || !Number.isFinite(value.startScalar)
            || typeof value.endScalar !== 'number' || !Number.isFinite(value.endScalar)) {
            throw new Error(`转换实体引用 Record projectionState 无效: ${path}`);
        }
        if (value.attachmentMode !== undefined
            && value.attachmentMode !== 'full-entity'
            && value.attachmentMode !== 'partial-range') {
            throw new Error(`转换实体引用 Record projectionState 无效: ${path}.attachmentMode`);
        }
        return;
    }
    if (projectionResultKind === 'arc' || projectionResultKind === 'ellipse-arc') {
        if (typeof value.startAngle !== 'number' || !Number.isFinite(value.startAngle)
            || typeof value.endAngle !== 'number' || !Number.isFinite(value.endAngle)
            || typeof value.clockwise !== 'boolean') {
            throw new Error(`转换实体引用 Record projectionState 无效: ${path}`);
        }
        return;
    }
    throw new Error(`转换实体引用 Record projectionState 类别无效: ${path}`);
}

/**
 * 校验持久化诊断快照。
 *
 * 未知离线 JSON 必须在进入 Runtime 前锁定诊断结构，否则关系面板可能在
 * Record 已写入仓储后才因坏字段失败，留下半恢复状态。
 *
 * @param value 未知诊断快照。
 * @param path 字段路径。
 */
function assertExecutionDiagnostic(value: unknown, path: string): void {
    if (value === undefined) {
        return;
    }
    if (!isRecord(value)) {
        throw new Error(`转换实体引用 Record diagnostic 无效: ${path}`);
    }
    assertStringEnum(value.kind, EXECUTION_DIAGNOSTIC_KINDS, `${path}.kind`);
    if (value.level !== 'warning' && value.level !== 'error') {
        throw new Error(`转换实体引用 Record diagnostic.level 无效: ${path}.level`);
    }
    parseNonEmptyString(value.message, `${path}.message`);
    assertOptionalNonEmptyString(value.referenceId, `${path}.referenceId`);
    assertOptionalNonEmptyString(value.sourceRefEntityId, `${path}.sourceRefEntityId`);
    assertOptionalNonEmptyString(value.targetSketchId, `${path}.targetSketchId`);
    assertOptionalNonEmptyString(value.targetEntityId, `${path}.targetEntityId`);
    assertOptionalNonEmptyString(value.relationId, `${path}.relationId`);
}

/**
 * 校验来源端点与目标端点的持久绑定。
 *
 * @param value 未知绑定数组。
 * @param path 字段路径。
 */
function assertEndpointAnchorBindings(value: unknown, path: string): void {
    if (value === undefined) {
        return;
    }
    if (!Array.isArray(value)) {
        throw new Error(`转换实体引用 Record endpointAnchorBindings 无效: ${path}`);
    }
    const bindingKeys = new Set<string>();
    value.forEach((binding, index) => {
        const bindingPath = `${path}[${index}]`;
        if (!isRecord(binding)) {
            throw new Error(`转换实体引用 Record endpointAnchorBindings 无效: ${bindingPath}`);
        }
        const sourceAnchorId = parseNonEmptyString(
            binding.sourceAnchorId,
            `${bindingPath}.sourceAnchorId`,
        );
        if (binding.targetEndpoint !== 'start' && binding.targetEndpoint !== 'end') {
            throw new Error(`转换实体引用 Record endpointAnchorBindings 无效: ${bindingPath}.targetEndpoint`);
        }
        const bindingKey = `${sourceAnchorId}::${binding.targetEndpoint}`;
        if (bindingKeys.has(bindingKey)) {
            throw new Error(`转换实体引用 Record endpointAnchorBindings 重复: ${bindingPath}`);
        }
        bindingKeys.add(bindingKey);
    });
}

/**
 * 校验 topology owner 的稳定持久身份。
 *
 * @param value 未知 owner 描述。
 * @param path 字段路径。
 */
function assertTopologyOwnerDescriptor(value: unknown, path: string): void {
    if (!isRecord(value)) {
        throw new Error(`转换实体引用 Record topology owner 无效: ${path}`);
    }
    assertStringEnum(value.ownerKind, TOPOLOGY_OWNER_KINDS, `${path}.ownerKind`);
    parseNonEmptyString(value.ownerId, `${path}.ownerId`);
    assertOptionalNonEmptyString(value.resultObjectId, `${path}.resultObjectId`);
    assertOptionalNonEmptyString(value.bodyBindingId, `${path}.bodyBindingId`);
    assertOptionalNonEmptyString(value.modelBodyId, `${path}.modelBodyId`);
    assertOptionalNonEmptyString(value.modelBodyRevisionId, `${path}.modelBodyRevisionId`);
}

/**
 * 校验 sampled-only 曲线的拟合元数据。
 *
 * @param value 未知拟合元数据。
 * @param path 字段路径。
 */
function assertTopologyFitMetadata(value: unknown, path: string): void {
    if (value === undefined) {
        return;
    }
    if (!isRecord(value)) {
        throw new Error(`转换实体引用 Record fitMetadata 无效: ${path}`);
    }
    assertStringEnum(value.fitKind, TOPOLOGY_FIT_KINDS, `${path}.fitKind`);
    assertStringEnum(value.fitMethod, TOPOLOGY_FIT_METHODS, `${path}.fitMethod`);
    if (typeof value.maxResidual !== 'number'
        || !Number.isFinite(value.maxResidual)
        || value.maxResidual < 0) {
        throw new Error(`转换实体引用 Record fitMetadata.maxResidual 无效: ${path}.maxResidual`);
    }
    assertPositiveInteger(value.sampledPointCount, `${path}.sampledPointCount`);
    if (value.controlPointCount !== undefined) {
        assertPositiveInteger(value.controlPointCount, `${path}.controlPointCount`);
    }
}

/**
 * 校验 topology 边重定位提示。
 *
 * @param value 未知匹配提示。
 * @param path 字段路径。
 */
function assertTopologyMatchHints(value: unknown, path: string): void {
    if (value === undefined) {
        return;
    }
    if (!isRecord(value)) {
        throw new Error(`转换实体引用 Record matchHints 无效: ${path}`);
    }
    assertOptionalStringArray(value.adjacentFaceRefs, `${path}.adjacentFaceRefs`);
    assertOptionalStringArray(
        value.adjacentFaceSubEntityIds,
        `${path}.adjacentFaceSubEntityIds`,
    );
}

/**
 * 校验 topology face 的持久来源描述。
 *
 * @param value 未知面来源。
 * @param path 字段路径。
 */
function assertTopologyFaceSourceDescriptor(value: unknown, path: string): void {
    if (!isRecord(value) || !isRecord(value.locator)) {
        throw new Error(`转换实体引用 Record topologyFaceSource 无效: ${path}`);
    }
    assertTopologyOwnerDescriptor(value.owner, `${path}.owner`);
    assertNonNegativeInteger(value.locator.faceIndex, `${path}.locator.faceIndex`);
    assertOptionalNonEmptyString(value.locator.semanticName, `${path}.locator.semanticName`);
    assertOptionalNonEmptyString(value.locator.persistentRef, `${path}.locator.persistentRef`);
    assertOptionalNonEmptyString(value.locator.subEntityId, `${path}.locator.subEntityId`);
    if (value.locator.surfaceKind !== undefined) {
        assertStringEnum(
            value.locator.surfaceKind,
            TOPOLOGY_SURFACE_KINDS,
            `${path}.locator.surfaceKind`,
        );
    }
    assertOptionalNullableNonEmptyString(
        value.locator.owningFeatureId,
        `${path}.locator.owningFeatureId`,
    );
}

/**
 * 校验投影结果与关系语义的确定对应关系。
 *
 * @param projectionResultKind 投影结果类别。
 * @param relationSemantics 未知关系语义。
 * @param path 字段路径。
 */
function assertRelationSemantics(
    projectionResultKind: string,
    relationSemantics: unknown,
    path: string,
): void {
    const expectedSemantics = projectionResultKind === 'line'
        ? 'collinear'
        : projectionResultKind === 'circle' || projectionResultKind === 'ellipse'
            ? 'coincident'
            : 'on-curve';
    if (relationSemantics !== expectedSemantics) {
        throw new Error(`转换实体引用 Record relationSemantics 无效: ${path}`);
    }
}

/**
 * 校验可选用户选择意图快照的稳定判别字段。
 *
 * @param value 未知选择意图。
 * @param path 字段路径。
 */
function assertSelectionIntent(value: unknown, path: string): void {
    if (value === undefined) {
        return;
    }
    if (!isRecord(value)) {
        throw new Error(`转换实体引用 Record selectionIntent 无效: ${path}`);
    }
    if (
        value.intentKind !== 'single-source'
        && value.intentKind !== 'sketch-chain'
        && value.intentKind !== 'topology-face'
        && value.intentKind !== 'topology-loop'
        && value.intentKind !== 'sketch-composite'
    ) {
        throw new Error(`转换实体引用 Record selectionIntent intentKind 无效: ${path}.intentKind`);
    }
    if (
        value.displayKind !== 'edge'
        && value.displayKind !== 'face'
        && value.displayKind !== 'loop'
        && value.displayKind !== 'chain'
    ) {
        throw new Error(`转换实体引用 Record selectionIntent displayKind 无效: ${path}.displayKind`);
    }
    parseNonEmptyString(value.intentId, `${path}.intentId`);
    parseNonEmptyString(value.displayLabel, `${path}.displayLabel`);
    if (typeof value.sourceIndex !== 'number' || !Number.isInteger(value.sourceIndex)
        || value.sourceIndex < 0
        || typeof value.sourceCount !== 'number' || !Number.isInteger(value.sourceCount)
        || value.sourceCount < 1
        || value.sourceIndex >= value.sourceCount) {
        throw new Error(`转换实体引用 Record selectionIntent 来源序号无效: ${path}`);
    }
    if (value.topologyFaceSource !== undefined) {
        assertTopologyFaceSourceDescriptor(
            value.topologyFaceSource,
            `${path}.topologyFaceSource`,
        );
    }
    if (value.topologyFaceRegionSources !== undefined) {
        if (!Array.isArray(value.topologyFaceRegionSources)
            || value.topologyFaceRegionSources.length === 0) {
            throw new Error(`转换实体引用 Record topologyFaceRegionSources 无效: ${path}.topologyFaceRegionSources`);
        }
        value.topologyFaceRegionSources.forEach((source, index) => {
            assertTopologyFaceSourceDescriptor(
                source,
                `${path}.topologyFaceRegionSources[${index}]`,
            );
        });
    }
}

/**
 * 校验 feature-edge 使用的稳定几何指纹。
 *
 * 几何指纹只描述来源曲线身份，不承载目标投影方向；因此圆弧指纹没有
 * `clockwise`，不能复用重建几何载荷的完整字段校验。
 *
 * @param value 未知几何指纹。
 * @param path 字段路径。
 */
function assertGeometryFingerprint(value: unknown, path: string): void {
    if (!isRecord(value)) {
        throw new Error(`转换实体引用 Record geomFingerprint 无效: ${path}`);
    }
    if (value.kind === 'line') {
        assertWorldCoordinate(value.startWorld, `${path}.startWorld`);
        assertWorldCoordinate(value.endWorld, `${path}.endWorld`);
        return;
    }
    if (value.kind === 'circle') {
        assertWorldCoordinate(value.centerWorld, `${path}.centerWorld`);
        assertFiniteNumber(value.radius, `${path}.radius`);
        return;
    }
    if (value.kind === 'arc') {
        assertWorldCoordinate(value.centerWorld, `${path}.centerWorld`);
        assertFiniteNumber(value.radius, `${path}.radius`);
        assertFiniteNumber(value.startAngle, `${path}.startAngle`);
        assertFiniteNumber(value.endAngle, `${path}.endAngle`);
        return;
    }
    throw new Error(`转换实体引用 Record geomFingerprint kind 无效: ${path}.kind`);
}

/**
 * 校验来源描述的判别字段与草图持久身份。
 *
 * @param value 未知来源描述。
 * @param path 字段路径。
 */
function assertSourceDescriptor(value: unknown, path: string): void {
    if (!isRecord(value)) {
        throw new Error(`转换实体引用 Record 来源必须是对象: ${path}`);
    }

    if (value.kind === 'sketch-entity') {
        assertSketchPersistentId(
            parseNonEmptyString(value.sourceSketchId, `${path}.sourceSketchId`),
            `${path}.sourceSketchId`,
        );
        assertSketchEntityPersistentId(
            parseNonEmptyString(value.entityId, `${path}.entityId`),
            `${path}.entityId`,
        );
        if (value.subEntity !== undefined) {
            if (!isRecord(value.subEntity)
                || value.subEntity.kind !== 'slot-edge') {
                throw new Error(`转换实体引用 Record 草图子实体定位无效: ${path}.subEntity`);
            }
            assertNonNegativeInteger(
                value.subEntity.edgeIndex,
                `${path}.subEntity.edgeIndex`,
            );
        }
        return;
    }

    if (value.kind === 'feature-edge') {
        if (!isRecord(value.locator)) {
            throw new Error(`转换实体引用 Record 特征边定位必须是对象: ${path}.locator`);
        }
        parseNonEmptyString(value.locator.owningObjectId, `${path}.locator.owningObjectId`);
        if (typeof value.locator.featureEdgeIndex !== 'number'
            || !Number.isInteger(value.locator.featureEdgeIndex)
            || value.locator.featureEdgeIndex < 0) {
            throw new Error(`转换实体引用 Record featureEdgeIndex 无效: ${path}.locator.featureEdgeIndex`);
        }
        assertGeometryFingerprint(
            value.locator.geomFingerprint,
            `${path}.locator.geomFingerprint`,
        );
        return;
    }

    if (value.kind === 'topology-edge') {
        if (!isRecord(value.owner) || !isRecord(value.locator)) {
            throw new Error(`转换实体引用 Record 拓扑来源不完整: ${path}`);
        }
        /**
         * topology locator 会在恢复阶段直接参与 owner/边重定位。这里必须先校验全部
         * 可选提示，避免 Runtime 已替换仓储后才在 checkpoint 读取坏结构。
         */
        assertTopologyOwnerDescriptor(value.owner, `${path}.owner`);
        assertNonNegativeInteger(value.locator.edgeIndex, `${path}.locator.edgeIndex`);
        assertOptionalNonEmptyString(value.locator.semanticName, `${path}.locator.semanticName`);
        assertOptionalNonEmptyString(value.locator.persistentRef, `${path}.locator.persistentRef`);
        assertOptionalNonEmptyString(value.locator.subEntityId, `${path}.locator.subEntityId`);
        if (value.locator.curveKind !== undefined) {
            assertStringEnum(
                value.locator.curveKind,
                TOPOLOGY_CURVE_KINDS,
                `${path}.locator.curveKind`,
            );
        }
        assertStringEnum(
            value.locator.resolutionLevel,
            TOPOLOGY_RESOLUTION_LEVELS,
            `${path}.locator.resolutionLevel`,
        );
        if (value.locator.geomFingerprint !== undefined) {
            assertGeometryFingerprint(
                value.locator.geomFingerprint,
                `${path}.locator.geomFingerprint`,
            );
        }
        assertTopologyFitMetadata(
            value.locator.fitMetadata,
            `${path}.locator.fitMetadata`,
        );
        assertTopologyMatchHints(
            value.locator.matchHints,
            `${path}.locator.matchHints`,
        );
        assertOptionalNonEmptyString(
            value.locator.topologyVersion,
            `${path}.locator.topologyVersion`,
        );
        assertOptionalNonEmptyString(
            value.originSelectionIntentId,
            `${path}.originSelectionIntentId`,
        );
        return;
    }

    throw new Error(`转换实体引用 Record 来源 kind 无效: ${path}.kind=${String(value.kind)}`);
}

/**
 * 校验并深拷贝单条引用 Record。
 *
 * @param value 未知 Record。
 * @param path 字段路径。
 * @returns 与调用方无共享引用的 Record plain data。
 */
function parseReferenceRecord(value: unknown, path: string): SketchEntityReferenceRecord {
    if (!isRecord(value) || !isSerializablePlainData(value)) {
        throw new Error(`转换实体引用 Record 必须是可序列化对象: ${path}`);
    }

    parseNonEmptyString(value.referenceId, `${path}.referenceId`);
    assertSketchPersistentId(
        parseNonEmptyString(value.targetSketchId, `${path}.targetSketchId`),
        `${path}.targetSketchId`,
    );
    assertSketchEntityPersistentId(
        parseNonEmptyString(value.targetEntityId, `${path}.targetEntityId`),
        `${path}.targetEntityId`,
    );
    assertSourceDescriptor(value.sourceDescriptor, `${path}.sourceDescriptor`);
    assertOptionalNonEmptyString(value.sourceDisplayLabel, `${path}.sourceDisplayLabel`);

    if (value.referenceMode !== 'sketch-entity-reference') {
        throw new Error(`转换实体引用 Record referenceMode 无效: ${path}.referenceMode`);
    }
    if (value.status !== 'active' && value.status !== 'dangling' && value.status !== 'failed') {
        throw new Error(`转换实体引用 Record status 无效: ${path}.status`);
    }
    const projectionResultKind = parseNonEmptyString(
        value.projectionResultKind,
        `${path}.projectionResultKind`,
    );
    if (
        projectionResultKind !== 'line'
        && projectionResultKind !== 'circle'
        && projectionResultKind !== 'ellipse'
        && projectionResultKind !== 'arc'
        && projectionResultKind !== 'ellipse-arc'
        && projectionResultKind !== 'parabola'
        && projectionResultKind !== 'spline'
        && projectionResultKind !== 'style-spline'
    ) {
        throw new Error(`转换实体引用 Record projectionResultKind 无效: ${path}.projectionResultKind`);
    }
    if (!isRecord(value.rebuildPayload)
        || value.rebuildPayload.projectionResultKind !== projectionResultKind
        || !isRecord(value.rebuildPayload.geometryPayload)) {
        throw new Error(`转换实体引用 Record rebuildPayload 无效: ${path}.rebuildPayload`);
    }
    assertGeometryPayload(
        projectionResultKind,
        value.rebuildPayload.geometryPayload,
        `${path}.rebuildPayload.geometryPayload`,
    );
    assertEndpointAnchorBindings(
        value.rebuildPayload.endpointAnchorBindings,
        `${path}.rebuildPayload.endpointAnchorBindings`,
    );
    const sourceKind = parseNonEmptyString(value.sourceKind, `${path}.sourceKind`);
    if (
        sourceKind !== 'line'
        && sourceKind !== 'circle'
        && sourceKind !== 'arc'
        && sourceKind !== 'ellipse'
        && sourceKind !== 'ellipse-arc'
        && sourceKind !== 'parabola'
        && sourceKind !== 'spline'
        && sourceKind !== 'style-spline'
    ) {
        throw new Error(`转换实体引用 Record sourceKind 无效: ${path}.sourceKind`);
    }
    const isSourceProjectionCombinationValid = (
        (sourceKind === 'line' && projectionResultKind === 'line')
        || (sourceKind === 'circle' && ['line', 'circle', 'ellipse'].includes(projectionResultKind))
        || (sourceKind === 'arc' && ['line', 'arc', 'ellipse-arc'].includes(projectionResultKind))
        || (sourceKind === 'ellipse' && ['line', 'ellipse'].includes(projectionResultKind))
        || (sourceKind === 'ellipse-arc' && ['line', 'ellipse-arc'].includes(projectionResultKind))
        || (sourceKind === 'parabola' && projectionResultKind === 'parabola')
        || (sourceKind === 'spline' && projectionResultKind === 'spline')
        || (sourceKind === 'style-spline' && projectionResultKind === 'style-spline')
    );
    if (!isSourceProjectionCombinationValid) {
        throw new Error(`转换实体引用 Record sourceKind/projectionResultKind 组合无效: ${path}`);
    }
    assertProjectionState(value.projectionState, projectionResultKind, `${path}.projectionState`);
    assertSelectionIntent(value.selectionIntent, `${path}.selectionIntent`);
    assertRelationSemantics(
        projectionResultKind,
        value.relationSemantics,
        `${path}.relationSemantics`,
    );
    assertExecutionDiagnostic(value.diagnostic, `${path}.diagnostic`);
    if (value.boundaryMemberState !== undefined
        && value.boundaryMemberState !== 'active'
        && value.boundaryMemberState !== 'detached') {
        throw new Error(`转换实体引用 Record boundaryMemberState 无效: ${path}.boundaryMemberState`);
    }

    // JSON 深拷贝同时清理对象上的显式 undefined，与正式持久化语义一致。
    return JSON.parse(JSON.stringify(value)) as SketchEntityReferenceRecord;
}

/**
 * 断言引用身份和存活目标在同一 Record 集合内唯一。
 *
 * @param records 已解析引用记录。
 */
function assertUniqueReferenceRecords(records: readonly SketchEntityReferenceRecord[]): void {
    const referenceIds = new Set<string>();
    const targetKeys = new Set<string>();
    records.forEach((record) => {
        if (referenceIds.has(record.referenceId)) {
            throw new Error(`转换实体引用 Record 存在重复 referenceId: ${record.referenceId}`);
        }
        referenceIds.add(record.referenceId);

        const targetKey = `${record.targetSketchId}::${record.targetEntityId}`;
        if (targetKeys.has(targetKey)) {
            throw new Error(`转换实体引用 Record 存在重复存活目标: ${targetKey}`);
        }
        targetKeys.add(targetKey);
    });
}

/**
 * 解析、校验并深拷贝转换实体引用 Record 集合。
 *
 * 该接口是持久化 Record 进入转换实体模块的唯一结构校验入口；调用方只负责自己的信封协议。
 *
 * @param value 未知 Record 数组。
 * @param path 字段路径。
 * @returns 保持输入顺序的完整 Record 深拷贝。
 */
export function parseSketchEntityReferenceRecords(
    value: unknown,
    path = 'records',
): SketchEntityReferenceRecord[] {
    if (!Array.isArray(value)) {
        throw new Error(`转换实体引用 Record 集合必须是数组: ${path}`);
    }
    const records = value.map((record, index) => (
        parseReferenceRecord(record, `${path}[${index}]`)
    ));
    assertUniqueReferenceRecords(records);
    return records;
}
